import {FieldError, Input, Label, TextField} from 'react-aria-components'

import ChevronDownSvg from '@/assets/icons/chevron-down.svg?react'
import InfoSvg from '@/assets/icons/info.svg?react'
import PriceHistorySvg from '@/assets/icons/price-history.svg?react'
import stripeTrustBadgePng from '@/assets/images/stripe-trust-badge.png'
import useIsAuthenticated from '@/auth/hooks/useIsAuthenticated'
import Price from '@/components/Price'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import Switch from '@/components/Switch/Switch'
import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import useFoodGrubhubCartSummary from '@/lib/food-grubhub/hooks/useFoodGrubhubCartSummary'
import {useDropOffNote} from '@/lib/food-grubhub/store'
import {useRoundUp} from '@/lib/payment/store'
import {FoodGrubhubCartRoute, FoodGrubhubPaymentRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {useIsMobile} from '@/utils/hooks/useWindowSize'
import useSearch from '@/utils/router/useSearch'
import FoodTips from '@/views/FoodGrubhub/components/FoodTips/FoodTips'

import style from './FoodGrubhubCart.module.scss'

export default memo(function FoodGrubhubCartSummary() {
  // const router = useRouter()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [isOpen, setIsOpen] = useState(!isMobile)
  // const [isCheckingOut, setIsCheckingOut] = useState(false)
  // const queryClient = useQueryClient()
  const isAuthenticated = useIsAuthenticated()
  const [isRewardsApplied, setIsRewardsApplied] = useSearch(FoodGrubhubCartRoute.id, 'applyRewards')
  const latestIsRewardsApplied = useLatest(isRewardsApplied)
  const [coupon, setCoupon] = useSearch(FoodGrubhubCartRoute.id, 'coupon')
  const [roundUp, setRoundUp] = useRoundUp()
  const [dropOffNote, setDropOffNote] = useDropOffNote()

  const {
    cashback,
    cartItemNumber,
    total,
    discountValue,
    derivedDiscountValue,
    subtotal,
    handleCouponBlur,
    couponError,
    handleCouponChange,
    couponInfo,
    isCouponLoading,
    roundUpAmount,
    totalAfterTax,
    deliveryFee,
    serviceFee,
    tax,
    isFetching,
    tips,
  } = useFoodGrubhubCartSummary(isRewardsApplied, coupon, setCoupon)

  const latestCoupon = useLatest(coupon)

  const isCouponValid = couponError === null

  // const successHref = useMemo(
  //   () =>
  //     router.buildLocation({
  //       to: CheckoutRoute.fullPath,
  //       params: {
  //         result: CheckoutResult.Success,
  //       },
  //       search: {
  //         guest: isAuthenticated ? true : undefined,
  //       },
  //     }).href,
  //   [router],
  // )

  // const failureHref = useMemo(
  //   () =>
  //     router.buildLocation({
  //       to: CartRoute.fullPath,
  //       search: {
  //         cancel: true,
  //       },
  //     }).href,
  //   [router],
  // )

  const onPress = useCallback(() => {
    void navigate({
      to: FoodGrubhubPaymentRoute.fullPath,
      search: {
        applyRewards: latestIsRewardsApplied.current,
        coupon: latestCoupon.current,
      },
    })

    // NOTE: below is the old flow
    // logEvent(...CHECKOUT_EVENT)
    // setIsCheckingOut(true)
    // announce('Checking out')

    // checkout({
    //   locale,
    //   successHref,
    //   failureHref,
    //   queryClient,
    //   applyRewards: latestIsRewardsApplied.current,
    // })
    //   .catch(error => {
    //     if (isCartCalibratedError(error)) {
    //       toastErrorMessage(buildStringForCalibratedError(error))
    //     } else {
    //       toastErrorMessage(error)
    //     }
    //   })
    //   .finally(() => {
    //     setIsCheckingOut(false)
    //   })
    //   .catch(() => {
    //     /* empty */
    //   })
  }, [navigate])

  return (
    <div className={style.CartSummary}>
      <TextField onChange={setDropOffNote} value={dropOffNote} className={style.InputField}>
        <Label>Drop-off notes</Label>
        <Input className={style.Input} placeholder='Add your notes here' />
      </TextField>
      <FoodTips />
      {/* <FoodTimeSelector /> */}
      <h2 className={style.Title}>
        Order summary{' '}
        <button
          className={style.ShowHide}
          aria-label='Order summary'
          aria-expanded={isOpen}
          onClick={() => {
            setIsOpen(isOpen => !isOpen)
          }}
        >
          <ChevronDownSvg className={style.Decoration} aria-hidden />
        </button>
      </h2>
      {isOpen && (
        <>
          <div className={style.SumCategory}>
            <span className={style.SumCategoryName}>
              Subtotal ({cartItemNumber} item{cartItemNumber > 1 && 's'})
            </span>
            <span className={clsx(style.SumCategoryValue, isFetching && style.Loading)}>
              <Price
                price={subtotal.toNumber()}
                options={{
                  fractionDigits: USD_DISPLAY_DECIMALS,
                  exactFractionDigits: true,
                }}
              />
            </span>
          </div>
          <div className={style.SumCategory}>
            <span className={style.SumCategoryName}>Delivery fee</span>
            <span className={clsx(style.SumCategoryValue, isFetching && style.Loading)}>
              <Price
                price={deliveryFee.toNumber()}
                options={{
                  fractionDigits: USD_DISPLAY_DECIMALS,
                  exactFractionDigits: true,
                }}
              />
            </span>
          </div>
          <div className={style.SumCategory}>
            <span className={style.SumCategoryName}>Service fee</span>
            <span className={clsx(style.SumCategoryValue, isFetching && style.Loading)}>
              <Price
                price={serviceFee.toNumber()}
                options={{
                  fractionDigits: USD_DISPLAY_DECIMALS,
                  exactFractionDigits: true,
                }}
              />
            </span>
          </div>
          <div className={style.Divider} />
          <div className={style.SumCategory}>
            <span className={style.SumCategoryName}>Tax</span>
            <span className={clsx(style.SumCategoryValue, isFetching && style.Loading)}>
              <Price
                price={tax.toNumber()}
                options={{
                  fractionDigits: USD_DISPLAY_DECIMALS,
                  exactFractionDigits: true,
                }}
              />
            </span>
          </div>
          <div className={style.SumCategory}>
            <span className={style.SumCategoryName}>Tips</span>
            <span className={clsx(style.SumCategoryValue, isFetching && style.Loading)}>
              <Price
                price={tips}
                options={{
                  fractionDigits: USD_DISPLAY_DECIMALS,
                  exactFractionDigits: true,
                }}
              />
            </span>
          </div>
          {roundUpAmount.gt(0) && (
            <>
              <div className={style.SumCategory}>
                <span className={clsx(style.SumCategoryName)}>Total after tax</span>
                <span className={clsx(style.SumCategoryValue, isFetching && style.Loading)}>
                  <Price
                    price={totalAfterTax}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </span>
              </div>
              <div className={clsx(style.SumCategory, style.Secondary)}>
                <div className={style.SwitchWrapper}>
                  <Switch
                    label={'Donate change to NFB'}
                    onChange={setRoundUp}
                    isSelected={roundUp}
                    isLabelVisible
                    labelClassName={style.SumCategoryName}
                  />
                  <a
                    href='https://blog.innosearch.ai/'
                    className={style.Info}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <InfoSvg aria-label='Read more about NFB change donation program' />
                  </a>
                </div>
                <span
                  className={clsx(
                    style.SumCategoryValue,
                    roundUp ? style.RoundedUp : style.UnRounded,
                    isFetching && style.Loading,
                  )}
                >
                  <Price
                    price={roundUpAmount}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </span>
              </div>
            </>
          )}
          {isAuthenticated && discountValue > 0 && (
            <div className={style.SumCategory}>
              <Switch
                label='Apply rewards'
                onChange={setIsRewardsApplied}
                isSelected={isRewardsApplied}
                isLabelVisible
                labelClassName={style.SumCategoryName}
              />
              {isRewardsApplied && (
                <span
                  className={clsx(
                    style.SumCategoryValue,
                    derivedDiscountValue > 0 && style.GoodValue,
                    isFetching && style.Loading,
                  )}
                >
                  <Price
                    price={-derivedDiscountValue}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </span>
              )}
            </div>
          )}
          <VisuallyHidden
            as='form'
            className={style.CouponForm}
            onSubmit={e => {
              e.preventDefault()
            }}
            isVisible={!isRewardsApplied}
          >
            <TextField
              name='coupon'
              onChange={handleCouponChange}
              onBlur={handleCouponBlur}
              validationBehavior='aria'
              isInvalid={!isCouponValid}
              type='text'
              className={style.Field}
              isDisabled={isRewardsApplied}
              defaultValue={coupon}
            >
              <VisuallyHidden as={Label}>Coupon code</VisuallyHidden>
              <Input className={style.Input} placeholder='Coupon code' />
              {couponError && (
                <FieldError className={style.Error}>{couponError.message}</FieldError>
              )}
            </TextField>
            <PrimaryButton
              type='submit'
              size='sm'
              className={style.ApplyBtn}
              isPending={isCouponLoading}
            >
              Apply
            </PrimaryButton>
          </VisuallyHidden>
          {couponInfo && !isRewardsApplied && (
            <div className={style.CouponInfo}>
              <div>Coupon applied</div>
              <div>
                <Price
                  price={-couponInfo.amount}
                  options={{
                    fractionDigits: USD_DISPLAY_DECIMALS,
                    exactFractionDigits: true,
                  }}
                />
              </div>
            </div>
          )}
        </>
      )}
      <div className={style.Divider} />
      <div className={style.Total}>
        <span>Total</span>
        <span className={clsx(isFetching && style.Loading)}>
          <Price
            price={total}
            options={{
              fractionDigits: USD_DISPLAY_DECIMALS,
              exactFractionDigits: true,
            }}
          />
        </span>
      </div>
      {isOpen && cashback && (
        <div className={style.SumCategory}>
          <span className={clsx(style.SumCategoryName, style.GoodValue, style.Strong)}>
            <PriceHistorySvg className={style.Decoration} aria-hidden />
            Rewards ({cashback.rate.mul(100).toNumber()}%)
          </span>
          <span
            className={clsx(style.SumCategoryValue, style.GoodValue, isFetching && style.Loading)}
          >
            <Price
              price={cashback.value}
              options={{
                fractionDigits: USD_DISPLAY_DECIMALS,
                exactFractionDigits: true,
              }}
            />
          </span>
        </div>
      )}
      <PrimaryButton
        className={style.PayBtn}
        onPress={onPress}
        isDisabled={cartItemNumber === 0 || isFetching}
      >
        Next step
      </PrimaryButton>
      {isOpen && <img src={stripeTrustBadgePng} alt='' />}
    </div>
  )
})
